import { MessageResponse, PrivatePhotoStatus } from "@/generated-api";
import {
  CheckCircle2Icon,
  EyeOffIcon,
  ImagesIcon,
  ImageUpIcon,
} from "lucide-react";
import TimeReadStatus from "./TimeReadStatus";
import { Button } from "../ui/button";
import {
  useGetProfileView,
  useSharePrivatePhotos,
  useUnsharePrivatePhotos,
} from "@/tanstack-hooks";
import SpinnerSmall from "../SpinnerSmall";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "@/hooks/useTranslation";

export default function PhotosRequestedChatBubble({
  counterpartyId,
  message,
}: {
  counterpartyId: string;
  message: MessageResponse;
}) {
  const { data, isLoading } = useGetProfileView(counterpartyId);
  const { toast } = useToast();
  const sharePrivatePhotosMutation = useSharePrivatePhotos();
  const unsharePrivatePhotosMutation = useUnsharePrivatePhotos();
  const t = useTranslation();

  const counterparty = message.sender_id === counterpartyId;

  const handleSharePrivatePhotos = () => {
    data && (data.my_photos_share_status = PrivatePhotoStatus.SHARED);
    sharePrivatePhotosMutation.mutate(counterpartyId, {
      onSuccess: () => {
        toast({
          variant: "default",
          // @ts-expect-error Type 'Element' is not assignable to type
          title: (
            <div className="flex items-center gap-2">
              <CheckCircle2Icon className="h-4 w-4" />
              <span>{t.PHOTO_SHARING.LABELS.SHARE_SUCCESS}</span>
            </div>
          ),
          className: "dark rounded-2xl",
          duration: 800,
        });
      },
      onError: (error) => {
        data && (data.my_photos_share_status = PrivatePhotoStatus.HIDDEN);
        console.error("Error sharing photos", error);
        toast({
          variant: "destructive",
          title: t.PHOTO_SHARING.ERRORS.SHARE,
          description: t.SHARED.ERRORS.PERSISTENT,
          className: "dark",
        });
      },
    });
  };

  const handleUnsharePrivatePhotos = () => {
    data && (data.my_photos_share_status = PrivatePhotoStatus.HIDDEN);
    unsharePrivatePhotosMutation.mutate(counterpartyId, {
      onSuccess: () => {
        toast({
          variant: "default",
          // @ts-expect-error Type 'Element' is not assignable to type
          title: (
            <div className="flex items-center gap-2">
              <EyeOffIcon className="h-4 w-4" />
              <span>{t.PHOTO_SHARING.LABELS.UNSHARE_SUCCESS}</span>
            </div>
          ),
          className: "dark rounded-2xl",
          duration: 800,
        });
      },
      onError: (error) => {
        data && (data.my_photos_share_status = PrivatePhotoStatus.SHARED);
        console.error("Error unsharing photos", error);
        toast({
          variant: "destructive",
          title: t.PHOTO_SHARING.ERRORS.UNSHARE,
          description: t.SHARED.ERRORS.PERSISTENT,
          className: "dark",
        });
      },
    });
  };

  return (
    <div
      className={`${
        counterparty ? "bg-white text-black" : "bg-zinc-800 text-white"
      } p-3 rounded-2xl inline-flex items-center gap-3 select-none`}
    >
      <div className="relative">
        <div className="text-base break-words whitespace-pre-wrap max-w-xs lg:max-w-md">
          {counterparty ? (
            <div className="flex flex-col">
              {t.PHOTO_SHARING.LABELS.REQUESTED_MESSAGE(message.content)}

              {isLoading ? (
                <Button
                  className="max-w-full mt-2 items-center"
                  variant="secondary"
                >
                  <SpinnerSmall />
                </Button>
              ) : data?.my_photos_share_status === PrivatePhotoStatus.SHARED ? (
                <Button
                  className="max-w-full mt-2 items-center"
                  variant="secondary"
                  onClick={handleUnsharePrivatePhotos}
                >
                  <EyeOffIcon className="h-6 w-6 mr-3" />
                  {t.PHOTO_SHARING.LABELS.UNSHARE_BUTTON}
                </Button>
              ) : (
                <Button
                  className="max-w-full mt-2 items-center"
                  variant="secondary"
                  onClick={handleSharePrivatePhotos}
                >
                  <ImageUpIcon className="h-6 w-6 mr-3" />
                  {t.PHOTO_SHARING.LABELS.SHARE_BUTTON}
                </Button>
              )}
            </div>
          ) : (
            <div className="flex flex-row gap-3">
              <ImagesIcon className="h-6 w-6 shrink-0" />
              {t.PHOTO_SHARING.LABELS.REQUESTED_MESSAGE()}
            </div>
          )}

          <span
            className={`inline-block ${
              counterparty
                ? "w-[2.4rem]"
                : message.read_at
                ? "w-[3.7rem]"
                : "w-[3.4rem]"
            }`}
          ></span>
        </div>
        <TimeReadStatus message={message} counterparty={counterparty} />
      </div>
    </div>
  );
}
